import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  TextField,
  Button,
  CircularProgress,
  Menu,
  MenuItem,
  Divider,
  Alert,
  Collapse,
} from '@mui/material';
import {
  ThumbUp,
  ThumbUpOutlined,
  MoreVert,
  Edit,
  Delete,
  Flag,
  Reply,
  ExpandMore,
  ExpandLess,
  VisibilityOff,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { usePostComments } from '../../hooks/useCommunity';
import {
  useCreateComment,
  useUpdateComment,
  useDeleteComment,
  useToggleCommentLike,
} from '../../hooks/useCommunityMutations';
import { getReplies } from '../../services/communityApi';
import type { CommunityComment } from '../../types/community';
import { COLORS } from '@elevair/shared';
import ConfirmDialog from '../common/ConfirmDialog';

const commentSchema = z.object({
  content: z
    .string()
    .min(1, 'Comment is required')
    .max(2000, 'Comment must be under 2000 characters')
    .transform((val) => val.trim()),
});

type CommentFormData = z.infer<typeof commentSchema>;

interface CommentItemProps {
  comment: CommunityComment;
  postId: string;
  onReport?: (comment: CommunityComment) => void;
  onHide?: (commentId: string) => void;
  showAdminActions?: boolean;
  depth?: number;
  canWrite?: boolean;
}

function CommentItem({
  comment,
  postId,
  onReport,
  onHide,
  showAdminActions = false,
  depth = 0,
  canWrite = true,
}: CommentItemProps) {
  const { user } = useAuth();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showReplies, setShowReplies] = useState(false);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [replies, setReplies] = useState<CommunityComment[]>([]);
  const [loadingReplies, setLoadingReplies] = useState(false);
  const [localRepliesCount, setLocalRepliesCount] = useState(comment.repliesCount || 0);

  const toggleLike = useToggleCommentLike();
  const updateComment = useUpdateComment();
  const deleteComment = useDeleteComment();
  const createComment = useCreateComment();

  const isAuthor = user?._id === comment.author._id;
  const isSuperAdmin = user?.role === 'super_admin';
  const maxDepth = 2; // Limit nesting depth

  const {
    control: editControl,
    handleSubmit: handleEditSubmit,
    reset: resetEdit,
    formState: { errors: editErrors },
  } = useForm<CommentFormData>({
    resolver: zodResolver(commentSchema),
    defaultValues: { content: comment.content },
  });

  const {
    control: replyControl,
    handleSubmit: handleReplySubmit,
    reset: resetReply,
    formState: { errors: replyErrors, isSubmitting: isReplying },
  } = useForm<CommentFormData>({
    resolver: zodResolver(commentSchema),
    defaultValues: { content: '' },
  });

  const handleLike = async () => {
    try {
      await toggleLike.mutateAsync(comment._id);
    } catch (err) {
      console.error('Failed to toggle like:', err);
    }
  };

  const handleEditSave = async (data: CommentFormData) => {
    setError(null);
    try {
      await updateComment.mutateAsync({
        id: comment._id,
        data: { content: data.content },
      });
      setIsEditing(false);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: { message?: string } } } };
      setError(error.response?.data?.error?.message || 'Failed to update comment');
    }
  };

  const handleDelete = async () => {
    setError(null);
    console.log('Deleting comment with ID:', comment._id, 'Full comment:', comment);
    try {
      await deleteComment.mutateAsync(comment._id);
      setDeleteConfirmOpen(false);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: { message?: string } } } };
      const errorMessage = error.response?.data?.error?.message || 'Failed to delete comment';
      setError(errorMessage);
      console.error('Failed to delete comment:', err, 'Comment ID was:', comment._id);
    }
  };

  const fetchReplies = async () => {
    if (loadingReplies) return;
    setLoadingReplies(true);
    try {
      const response = await getReplies(comment._id);
      if (response.success && response.data.replies) {
        setReplies(response.data.replies as CommunityComment[]);
      }
    } catch (err) {
      console.error('Failed to fetch replies:', err);
    } finally {
      setLoadingReplies(false);
    }
  };

  const handleToggleReplies = async () => {
    if (!showReplies && replies.length === 0 && localRepliesCount > 0) {
      await fetchReplies();
    }
    setShowReplies(!showReplies);
  };

  const handleReply = async (data: CommentFormData) => {
    setError(null);
    try {
      await createComment.mutateAsync({
        postId,
        data: {
          content: data.content,
          parentCommentId: comment._id,
        },
      });
      resetReply();
      setShowReplyForm(false);
      // Increment local reply count and fetch fresh replies
      setLocalRepliesCount((prev) => prev + 1);
      await fetchReplies();
      setShowReplies(true);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: { message?: string } } } };
      setError(error.response?.data?.error?.message || 'Failed to post reply');
    }
  };

  const getAvatarColor = (id: string) => {
    const colors = [COLORS.primary, COLORS.secondary, COLORS.accent, '#9C27B0', '#FF5722'];
    const index = id.charCodeAt(0) % colors.length;
    return colors[index];
  };

  return (
    <Box
      sx={{
        pl: depth > 0 ? 4 : 0,
        borderLeft: depth > 0 ? `2px solid ${COLORS.primary}20` : 'none',
        ml: depth > 0 ? 2 : 0,
        opacity: comment.isHidden ? 0.6 : 1,
      }}
    >
      <Box display="flex" gap={1.5} py={1.5}>
        <Avatar
          sx={{
            bgcolor: comment.author.avatar ? 'transparent' : getAvatarColor(comment.author._id),
            width: 32,
            height: 32,
            fontSize: '0.875rem',
          }}
          src={comment.author.avatar}
        >
          {comment.author.firstName?.charAt(0)}
          {comment.author.lastName?.charAt(0)}
        </Avatar>

        <Box flex={1}>
          {/* Comment Header */}
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="subtitle2" fontWeight={600}>
                {comment.author.firstName} {comment.author.lastName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })}
              </Typography>
              {comment.updatedAt !== comment.createdAt && (
                <Typography variant="caption" color="text.secondary">
                  (edited)
                </Typography>
              )}
              {comment.isHidden && showAdminActions && (
                <Box display="flex" alignItems="center" gap={0.5} color="warning.main">
                  <VisibilityOff sx={{ fontSize: 14 }} />
                  <Typography variant="caption">Hidden</Typography>
                </Box>
              )}
            </Box>

            <IconButton
              size="small"
              onClick={(e) => setMenuAnchor(e.currentTarget)}
            >
              <MoreVert fontSize="small" />
            </IconButton>
          </Box>

          {/* Comment Content */}
          {isEditing ? (
            <form onSubmit={handleEditSubmit(handleEditSave)}>
              {error && (
                <Alert severity="error" sx={{ mb: 1, py: 0 }}>
                  {error}
                </Alert>
              )}
              <Controller
                name="content"
                control={editControl}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline
                    rows={2}
                    size="small"
                    error={!!editErrors.content}
                    helperText={editErrors.content?.message}
                    sx={{ mb: 1 }}
                  />
                )}
              />
              <Box display="flex" gap={1}>
                <Button size="small" onClick={() => { setIsEditing(false); resetEdit(); }}>
                  Cancel
                </Button>
                <Button
                  size="small"
                  type="submit"
                  variant="contained"
                  disabled={updateComment.isPending}
                >
                  {updateComment.isPending ? 'Saving...' : 'Save'}
                </Button>
              </Box>
            </form>
          ) : (
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {comment.content}
            </Typography>
          )}

          {/* Comment Actions */}
          {!isEditing && (
            <Box display="flex" alignItems="center" gap={2} mt={1}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  cursor: canWrite ? 'pointer' : 'default',
                  color: comment.isLiked ? COLORS.primary : 'text.secondary',
                  opacity: canWrite ? 1 : 0.7,
                  '&:hover': canWrite ? { color: COLORS.primary } : {},
                }}
                onClick={canWrite ? handleLike : undefined}
              >
                {comment.isLiked ? (
                  <ThumbUp sx={{ fontSize: 16 }} />
                ) : (
                  <ThumbUpOutlined sx={{ fontSize: 16 }} />
                )}
                <Typography variant="caption">{comment.likesCount}</Typography>
              </Box>

              {depth < maxDepth && canWrite && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    cursor: 'pointer',
                    color: 'text.secondary',
                    '&:hover': { color: COLORS.secondary },
                  }}
                  onClick={() => setShowReplyForm(!showReplyForm)}
                >
                  <Reply sx={{ fontSize: 16 }} />
                  <Typography variant="caption">Reply</Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Error message for delete/other actions */}
          {error && !isEditing && !showReplyForm && (
            <Alert severity="error" sx={{ mt: 1, py: 0 }}>
              {error}
            </Alert>
          )}

          {/* Reply Form */}
          <Collapse in={showReplyForm}>
            <Box mt={2}>
              {error && (
                <Alert severity="error" sx={{ mb: 1, py: 0 }}>
                  {error}
                </Alert>
              )}
              <form onSubmit={handleReplySubmit(handleReply)}>
                <Controller
                  name="content"
                  control={replyControl}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      multiline
                      rows={2}
                      size="small"
                      placeholder="Write a reply..."
                      error={!!replyErrors.content}
                      helperText={replyErrors.content?.message}
                      sx={{ mb: 1 }}
                    />
                  )}
                />
                <Box display="flex" gap={1}>
                  <Button size="small" onClick={() => { setShowReplyForm(false); resetReply(); }}>
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    type="submit"
                    variant="contained"
                    disabled={isReplying}
                  >
                    {isReplying ? 'Posting...' : 'Reply'}
                  </Button>
                </Box>
              </form>
            </Box>
          </Collapse>

          {/* Replies */}
          {localRepliesCount > 0 && (
            <Box mt={1}>
              <Button
                size="small"
                startIcon={loadingReplies ? <CircularProgress size={14} /> : (showReplies ? <ExpandLess /> : <ExpandMore />)}
                onClick={handleToggleReplies}
                disabled={loadingReplies}
                sx={{ color: 'text.secondary' }}
              >
                {showReplies ? 'Hide' : 'View'} {localRepliesCount} {localRepliesCount === 1 ? 'reply' : 'replies'}
              </Button>
              <Collapse in={showReplies}>
                {replies.map((reply) => (
                  <CommentItem
                    key={reply._id}
                    comment={reply}
                    postId={postId}
                    onReport={onReport}
                    onHide={onHide}
                    showAdminActions={showAdminActions}
                    depth={depth + 1}
                    canWrite={canWrite}
                  />
                ))}
              </Collapse>
            </Box>
          )}
        </Box>
      </Box>

      {/* Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        {isAuthor && (
          <MenuItem onClick={() => { setMenuAnchor(null); setIsEditing(true); }}>
            <Edit fontSize="small" sx={{ mr: 1 }} />
            Edit
          </MenuItem>
        )}
        {isAuthor && (
          <MenuItem
            onClick={() => { setMenuAnchor(null); setDeleteConfirmOpen(true); }}
            sx={{ color: 'error.main' }}
          >
            <Delete fontSize="small" sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        )}
        {!isAuthor && (
          <MenuItem onClick={() => { setMenuAnchor(null); onReport?.(comment); }}>
            <Flag fontSize="small" sx={{ mr: 1 }} />
            Report
          </MenuItem>
        )}
        {showAdminActions && isSuperAdmin && !comment.isHidden && (
          <>
            <Divider />
            <MenuItem onClick={() => { setMenuAnchor(null); onHide?.(comment._id); }}>
              <VisibilityOff fontSize="small" sx={{ mr: 1 }} />
              Hide Comment
            </MenuItem>
          </>
        )}
      </Menu>

      <ConfirmDialog
        open={deleteConfirmOpen}
        title="Delete Comment"
        message="Are you sure you want to delete this comment?"
        confirmText="Delete"
        variant="danger"
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirmOpen(false)}
        loading={deleteComment.isPending}
      />
    </Box>
  );
}

interface CommentSectionProps {
  postId: string;
  onReportComment?: (comment: CommunityComment) => void;
  onHideComment?: (commentId: string) => void;
  showAdminActions?: boolean;
  onCommentPosted?: () => void;
  canWrite?: boolean;
}

export default function CommentSection({
  postId,
  onReportComment,
  onHideComment,
  showAdminActions = false,
  onCommentPosted,
  canWrite = true,
}: CommentSectionProps) {
  const [error, setError] = useState<string | null>(null);
  const { data, isLoading, isError } = usePostComments(postId);
  const createComment = useCreateComment();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CommentFormData>({
    resolver: zodResolver(commentSchema),
    defaultValues: { content: '' },
  });

  const onSubmit = async (formData: CommentFormData) => {
    setError(null);
    try {
      await createComment.mutateAsync({
        postId,
        data: { content: formData.content },
      });
      reset();
      onCommentPosted?.();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: { message?: string } } } };
      setError(error.response?.data?.error?.message || 'Failed to post comment');
    }
  };

  const comments = data?.data?.comments || [];

  return (
    <Box>
      {/* Comment Form - only show for users with write access */}
      {canWrite ? (
        <Box mb={3}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <form onSubmit={handleSubmit(onSubmit)}>
            <Controller
              name="content"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="Write a comment..."
                  error={!!errors.content}
                  helperText={errors.content?.message}
                  sx={{ mb: 1 }}
                />
              )}
            />
            <Box display="flex" justifyContent="flex-end">
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting}
                size="small"
                sx={{
                  backgroundColor: COLORS.accent,
                  '&:hover': { backgroundColor: COLORS.accent, opacity: 0.9 },
                }}
              >
                {isSubmitting ? (
                  <>
                    <CircularProgress size={16} sx={{ mr: 1 }} color="inherit" />
                    Posting...
                  </>
                ) : (
                  'Comment'
                )}
              </Button>
            </Box>
          </form>
        </Box>
      ) : (
        <Alert severity="info" sx={{ mb: 2 }}>
          Upgrade to Rise tier to comment on posts.
        </Alert>
      )}

      <Divider sx={{ mb: 2 }} />

      {/* Comments List */}
      {isLoading ? (
        <Box display="flex" justifyContent="center" py={3}>
          <CircularProgress size={24} />
        </Box>
      ) : isError ? (
        <Alert severity="error">Failed to load comments</Alert>
      ) : comments.length === 0 ? (
        <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
          No comments yet. Be the first to comment!
        </Typography>
      ) : (
        comments.map((comment) => (
          <CommentItem
            key={comment._id}
            comment={comment}
            postId={postId}
            onReport={onReportComment}
            onHide={onHideComment}
            showAdminActions={showAdminActions}
            canWrite={canWrite}
          />
        ))
      )}
    </Box>
  );
}
